import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { Upload as UploadIcon } from '@mui/icons-material';

interface UploadOrgPictureModalProps {
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
  currentLogoUrl?: string;
}

export const UploadOrgPictureModal: React.FC<UploadOrgPictureModalProps> = ({
  onClose,
  onSubmit,
  currentLogoUrl,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setModalError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setModalError('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setModalError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    setModalError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(selectedFile);
      onClose();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to upload profile picture');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setModalError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">Upload Profile Picture</h2>
          <button onClick={handleClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-4">
          {/* Current Logo */}
          {currentLogoUrl && !previewUrl && (
            <div>
              <label className="block text-white text-sm font-medium mb-2">Current Logo</label>
              <div className="flex justify-center">
                <img
                  src={currentLogoUrl}
                  alt="Current logo"
                  className="w-32 h-32 object-cover rounded-lg border border-white/10"
                />
              </div>
            </div>
          )}

          {/* Preview */}
          {previewUrl && (
            <div>
              <label className="block text-white text-sm font-medium mb-2">Preview</label>
              <div className="flex justify-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg border border-white/10"
                />
              </div>
            </div>
          )}

          {/* File Input */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              {selectedFile ? 'Change Image' : 'Select Image'}
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="org-picture-upload"
              />
              <label
                htmlFor="org-picture-upload"
                className="flex items-center justify-center gap-2 w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white hover:bg-white/10 cursor-pointer transition-colors"
              >
                <UploadIcon className="w-5 h-5" />
                <span>{selectedFile ? selectedFile.name : 'Choose File'}</span>
              </label>
            </div>
            <p className="text-white/40 text-xs mt-1">
              PNG, JPG or GIF. Max 5MB.
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleClose} variant="ghost" className="flex-1">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="default"
            className="flex-1"
            disabled={!selectedFile || isSubmitting}
          >
            {isSubmitting ? 'Uploading...' : 'Upload Picture'}
          </Button>
        </div>
      </div>
    </div>
  );
};